// Flexible JSON parse (handles plain, escaped, double-encoded, or substring JSON)
export function tryParseJsonFlexible(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;

  let s = value.trim();

  // strip wrapping quotes if present
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    s = s.slice(1, -1);
  }

  try { return JSON.parse(s); } catch (e) {}

  // try unescape common sequences then parse
  try {
    const unescaped = s
      .replace(/\\"/g, '"')
      .replace(/\\n/g, '')
      .replace(/\\r/g, '')
      .replace(/\\t/g, '')
      .replace(/\\\\/g, '\\');
    return JSON.parse(unescaped);
  } catch (e) {}

  // try substring JSON
  const first = s.indexOf('{');
  const last = s.lastIndexOf('}');
  if (first !== -1 && last !== -1 && last > first) {
    const sub = s.slice(first, last + 1);
    try { return JSON.parse(sub); } catch (e) {}
  }

  // try double-encoded JSON
  try {
    const once = JSON.parse(s);
    if (typeof once === 'string') {
      try { return JSON.parse(once); } catch (e) {}
    } else {
      return once;
    }
  } catch (e) {}

  return null;
}

export function normalizeRegistrationRow(obj) {
  if (!obj || typeof obj !== 'object') return {};
  const out = { ...obj };

  // detect a "raw/payload" column
  const rawKey = Object.keys(out).find(k => /raw|payload/i.test(k));
  const rawVal = rawKey ? out[rawKey] : undefined;

  const parsed = tryParseJsonFlexible(rawVal);
  if (parsed && typeof parsed === 'object') {
    Object.assign(out, parsed);
    out.__original = obj;
    out.__originalRaw = rawVal || '';
  } else {
    out.__originalRaw = rawVal || '';
  }

  // basic normalization / fallbacks
  const nameFromNameField = out['Name'] ? String(out['Name']) : '';
  const split = nameFromNameField ? nameFromNameField.split(' ') : [];

  out.firstName = out.firstName || out.FirstName || split[0] || '';
  out.lastName = out.lastName || out.LastName || split.slice(1).join(' ') || '';
  out.email = out.email || out.Email || out.emailAddress || '';
  out.phone = out.phone || out.Phone || out['Phone Number'] || out.phoneNumber || '';
  out.dateTime = out.dateTime || out.DateTime || out.Timestamp || out['Timestamp'] || '';

  // numeric age if present
  if (out.age !== undefined && out.age !== null && !Number.isNaN(Number(out.age))) {
    out._age = Number(out.age);
  }

  // normalize DOB fields
  out._dob = out.dob || out.DOB || out['Date of Birth'] || out.dateOfBirth || out['DOB'] || '';

  // normalize dorm/rooming fields for youth sub-grouping
  out._dorm =
    out.dormChoice ||
    out['Dorm Choice'] ||
    out.roomingWithOthers ||
    out['Staying on Grounds/Rooming with others'] ||
    out.roommates ||
    out['Roomates/Staying With'] ||
    out.stayingWith ||
    out.rooming ||
    '';

  return out;
}

export function buildRegistrationsFromResponse(resp) {
  if (!resp) return [];
  if (Array.isArray(resp.registrations)) return resp.registrations.map(normalizeRegistrationRow);

  // sheet-style { headers, rows }
  if (Array.isArray(resp.headers) && Array.isArray(resp.rows)) {
    const headers = resp.headers.map(h => (typeof h === 'string' ? h.trim() : h));
    return resp.rows.map((row) => {
      if (Array.isArray(row)) {
        const obj = {};
        headers.forEach((h, i) => { obj[h] = row[i]; });
        return normalizeRegistrationRow(obj);
      }
      return normalizeRegistrationRow(row);
    });
  }

  if (Array.isArray(resp)) return resp.map(normalizeRegistrationRow);

  return [];
}

export function calculateAgeFromDob(dateObj) {
  const today = new Date();
  let age = today.getFullYear() - dateObj.getFullYear();
  const m = today.getMonth() - dateObj.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dateObj.getDate())) age--;
  return age;
}

export function parseAgeFromRegRow(row) {
  if (!row) return null;
  if (typeof row._age === 'number' && !Number.isNaN(row._age)) return row._age;
  if (row.age !== undefined && row.age !== null && !Number.isNaN(Number(row.age))) return Number(row.age);

  const dobVal = String(
    row._dob || row.dob || row.DOB || row['DOB'] || row.dateOfBirth || row['Date of Birth'] || ''
  ).trim();

  if (!dobVal) return null;

  const iso = new Date(dobVal);
  if (!Number.isNaN(iso.getTime())) return calculateAgeFromDob(iso);

  const m = dobVal.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (m) {
    const mm = Number(m[1]) - 1;
    const dd = Number(m[2]);
    let yyyy = Number(m[3]);
    if (yyyy < 100) yyyy += (yyyy < 50 ? 2000 : 1900);
    const dt = new Date(yyyy, mm, dd);
    if (!Number.isNaN(dt.getTime())) return calculateAgeFromDob(dt);
  }

  return null;
}

export function categorizeAge(age) {
  if (age === null || age === undefined || Number.isNaN(Number(age))) return 'Unknown';
  const a = Number(age);
  if (a >= 0 && a <= 12) return 'Kids 0-12';
  if (a >= 13 && a <= 18) return 'Youth 13-18';
  if (a >= 19 && a <= 22) return 'College 19-22';
  if (a >= 23 && a <= 30) return 'Young Adults 23-30';
  if (a >= 31) return 'Adults 31+';
  return 'Unknown';
}

export function determineYouthRooming(row) {
  if (!row) return 'Other';
  const v = String(row._dorm || '').toLowerCase().trim();
  if (!v) return 'Other';
  if (v.includes('parent') || v.includes('with parent') || v === 'parent') return 'With Parent';
  if (v.includes('girl') || v.includes('female') || v.includes('girls') || v.includes('women')) return 'Girls Dorm';
  if (v.includes('boy') || v.includes('male') || v.includes('boys') || v.includes('men')) return 'Boys Dorm';
  return 'Other';
}

export function groupRegistrationsByAge(list) {
  const groups = {};

  list.forEach(r => {
    const age = parseAgeFromRegRow(r);
    const cat = categorizeAge(age);
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(r);
  });

  // add youth subgroups
  if (groups['Youth 13-18']) {
    const youth = groups['Youth 13-18'];
    const sub = { 'Girls Dorm': [], 'Boys Dorm': [], 'With Parent': [], 'Other': [] };
    youth.forEach(r => {
      const key = determineYouthRooming(r) || 'Other';
      (sub[key] || (sub[key] = [])).push(r);
    });
    groups['Youth 13-18'] = sub;
  }

  const order = ['Kids 0-12', 'Youth 13-18', 'College 19-22', 'Young Adults 23-30', 'Adults 31+', 'Unknown'];
  const ordered = {};
  order.forEach(k => { if (groups[k]) ordered[k] = groups[k]; });
  Object.keys(groups).forEach(k => { if (!ordered[k]) ordered[k] = groups[k]; });
  return ordered;
}